import { buildPayloadApiUrl, payloadFetch } from "./client";
import { mapEgdGamesResponse } from "src/utils/mapEgdGame";
import { mapEgdJackpotsResponse } from "src/utils/mapEgdJackpot";

/** @typedef {'new-and-popular' | 'progressive-jackpots' | 'cny'} EgdHubViewSlug */

/**
 * @typedef {Object} EgdHubCard
 * @property {import('src/utils/mapEgdGame').EgdGameCard} game
 * @property {import('src/utils/mapEgdJackpot').EgdJackpotMeter | null} [jackpot]
 */

/**
 * Fetches featured EGD games from pg-payload-cms.
 *
 * @param {'en' | 'fr'} locale
 * @param {{ status?: string }} [options] - Optional raw Payload `gameStatus` value
 *   (e.g. `new`, `cny`) to filter server-side instead of fetching every game.
 * @returns {Promise<import('src/utils/mapEgdGame').EgdGameCard[]>}
 */
export async function fetchEgdGames(locale, options = {}) {
  const { status } = options;
  const url = buildPayloadApiUrl("/api/egd-games", {
    locale,
    limit: 200,
    depth: 0,
    sort: "_order",
    "where[gameStatus][equals]": status,
  });

  const response = await payloadFetch(url);
  return mapEgdGamesResponse(response, locale);
}

/**
 * Fetches active progressive jackpot meters from pg-payload-cms, with each
 * meter's `game` (and `game.imageMedia`) resolved server-side via `depth=2` —
 * no separate game-list fetch or client-side name/id matching needed to
 * attach a game to its jackpot meter.
 *
 * @param {'en' | 'fr'} locale
 * @returns {Promise<import('src/utils/mapEgdJackpot').EgdJackpotMeter[]>}
 */
export async function fetchEgdJackpotMeters(locale) {
  const url = buildPayloadApiUrl("/api/egd-jackpot-meters", {
    "where[active][equals]": true,
    limit: 500,
    depth: 2,
    sort: "game",
    locale,
  });

  const response = await payloadFetch(url);
  return mapEgdJackpotsResponse(response, locale);
}
